import React, { useEffect, useMemo, useState } from 'react'
import { Alert, App as AntdApp, Empty, Select, Spin, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { DetectionAnnotationEditor } from '../../../components/history/HistoryDetail/DetectionAnnotationEditor'
import {
    getModelEvaluationImages,
    getModelEvaluationMatches,
} from '../../../services/modelManagementService'
import { getValidationOptions } from '../../../services/modelValidationService'
import {
    normalizeManagedObjectKey,
    resolveManagedObjectUrls,
} from '../../../services/ossService'
import type {
    HistoryLabelOption,
    HistoryPrediction,
} from '../../../types/history'
import type {
    ModelDisplayCategorySummary,
    ModelEvaluationImageResultSummary,
} from '../../../types/modelManagement'
import type {
    ModelEvaluationMatch,
    ValidationMatchOutcome,
    ValidationSchemeSide,
} from '../../../types/modelValidation'
import { getImageUrl } from '../../../utils/urlHelper'
import { getModelManagementErrorDetail } from '../modelManagement/pageConfig'
import { buildValidationLabelOptions } from './modelValidationUtils'

const IMAGE_PAGE_SIZE = 200
const MATCH_PAGE_SIZE = 500
const problemOutcomes = [
    'false_negative',
    'false_positive',
    'misclassified',
] as const

type ProblemOutcome = (typeof problemOutcomes)[number]
type ComparisonFilter = 'all' | ProblemOutcome

const getImageObjectKey = (
    image: ModelEvaluationImageResultSummary | null,
): string | null =>
    image?.sample?.image_object_key || image?.sample?.image_path || null

const matchesToPredictions = (
    matches: ModelEvaluationMatch[],
    useTruth: boolean,
): HistoryPrediction[] =>
    matches.flatMap((match) => {
        const bbox = useTruth ? match.truth_bbox : match.prediction_bbox
        const category = useTruth
            ? match.truth_category_key
            : match.prediction_category_key
        if (!bbox || !category) return []
        return [
            {
                class: category,
                confidence: useTruth ? 1 : match.prediction_confidence || 0,
                eng_class: category,
                display_category_key: category,
                bbox,
            },
        ]
    })

const getPredictionCategory = (prediction: HistoryPrediction): string =>
    prediction.display_category_key || prediction.eng_class || prediction.class

const deduplicatePredictions = (
    predictions: HistoryPrediction[],
): HistoryPrediction[] => {
    const uniquePredictions = new Map<string, HistoryPrediction>()
    predictions.forEach((prediction) => {
        const bbox = prediction.bbox
        if (!bbox) return
        const category = getPredictionCategory(prediction)
        uniquePredictions.set(
            [category, bbox.x1, bbox.y1, bbox.x2, bbox.y2].join(':'),
            prediction,
        )
    })
    return [...uniquePredictions.values()]
}

const loadAllEvaluationImages = async (
    taskId: number,
): Promise<ModelEvaluationImageResultSummary[]> => {
    const firstPage = await getModelEvaluationImages(taskId, {
        limit: IMAGE_PAGE_SIZE,
        offset: 0,
    })
    const items = [...firstPage.items]
    const total = firstPage.total ?? items.length

    while (items.length < total) {
        const nextPage = await getModelEvaluationImages(taskId, {
            limit: IMAGE_PAGE_SIZE,
            offset: items.length,
        })
        if (nextPage.items.length === 0) break
        items.push(...nextPage.items)
    }
    return items
}

const loadAllEvaluationMatches = async (
    taskId: number,
    params: {
        categoryKey?: string
        imageResultId?: number
        outcome?: ValidationMatchOutcome
        schemeSide?: ValidationSchemeSide
    },
): Promise<ModelEvaluationMatch[]> => {
    const firstPage = await getModelEvaluationMatches(taskId, {
        ...params,
        limit: MATCH_PAGE_SIZE,
        offset: 0,
    })
    const items = [...firstPage.items]

    while (items.length < firstPage.total) {
        const nextPage = await getModelEvaluationMatches(taskId, {
            ...params,
            limit: MATCH_PAGE_SIZE,
            offset: items.length,
        })
        if (nextPage.items.length === 0) break
        items.push(...nextPage.items)
    }
    return items
}

export interface ModelEvaluationComparisonFocus {
    categoryKey?: string
    outcome: ValidationMatchOutcome
    schemeSide?: ValidationSchemeSide
}

interface ModelEvaluationComparisonProps {
    focus?: ModelEvaluationComparisonFocus
    taskId: number
}

const ModelEvaluationComparison: React.FC<ModelEvaluationComparisonProps> = ({
    focus,
    taskId,
}) => {
    const { message } = AntdApp.useApp()
    const { i18n, t } = useTranslation(['admin'])
    const [images, setImages] = useState<ModelEvaluationImageResultSummary[]>(
        [],
    )
    const [comparisonFilter, setComparisonFilter] =
        useState<ComparisonFilter>('all')
    const [comparisonFilterResult, setComparisonFilterResult] = useState<{
        imageIds: Set<number>
        matches: ModelEvaluationMatch[]
        signature: string
    } | null>(null)
    const [comparisonImageId, setComparisonImageId] = useState<number>()
    const [comparisonMatches, setComparisonMatches] = useState<
        ModelEvaluationMatch[]
    >([])
    const [displayCategories, setDisplayCategories] = useState<
        ModelDisplayCategorySummary[]
    >([])
    const [imageUrlMap, setImageUrlMap] = useState<Map<string, string>>(
        new Map(),
    )
    const [loading, setLoading] = useState(true)
    const [filterLoading, setFilterLoading] = useState(false)
    const activeFocusOutcome =
        focus?.outcome ??
        (comparisonFilter === 'all' ? undefined : comparisonFilter)
    const activeFocusSchemeSide = focus?.schemeSide
    const activeFocusCategoryKey = focus?.categoryKey
    const isPairwiseFocus = Boolean(activeFocusSchemeSide)
    const activeFocusSignature = activeFocusOutcome
        ? [
              activeFocusSchemeSide || 'both',
              activeFocusOutcome,
              activeFocusCategoryKey || 'all',
          ].join(':')
        : ''

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        void loadAllEvaluationImages(taskId)
            .then(async (items) => {
                if (cancelled) return
                setImages(items)
                const objectKeys = items
                    .map((item) => getImageObjectKey(item))
                    .filter(Boolean) as string[]
                if (objectKeys.length > 0) {
                    const urls = await resolveManagedObjectUrls(objectKeys)
                    if (!cancelled) setImageUrlMap(urls)
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    message.error(
                        getModelManagementErrorDetail(
                            error,
                            t(
                                'admin:modelValidation.messages.loadReportFailed',
                            ),
                            t,
                        ),
                    )
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [message, t, taskId])

    useEffect(() => {
        let cancelled = false
        void getValidationOptions()
            .then((options) => {
                if (!cancelled) {
                    setDisplayCategories(options.display_categories)
                }
            })
            .catch(() => {
                if (!cancelled) setDisplayCategories([])
            })
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!activeFocusOutcome) {
            setComparisonFilterResult(null)
            setFilterLoading(false)
            return undefined
        }

        let cancelled = false
        setComparisonFilterResult(null)
        setFilterLoading(true)
        void loadAllEvaluationMatches(taskId, {
            categoryKey: activeFocusCategoryKey,
            outcome: activeFocusOutcome,
            schemeSide: activeFocusSchemeSide,
        })
            .then((matches) => {
                if (!cancelled) {
                    setComparisonFilterResult({
                        imageIds: new Set(
                            matches.map((match) => match.image_result_id),
                        ),
                        matches,
                        signature: activeFocusSignature,
                    })
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    setComparisonFilterResult({
                        imageIds: new Set(),
                        matches: [],
                        signature: activeFocusSignature,
                    })
                    message.error(
                        getModelManagementErrorDetail(
                            error,
                            t(
                                'admin:modelValidation.messages.loadComparisonFailed',
                            ),
                            t,
                        ),
                    )
                }
            })
            .finally(() => {
                if (!cancelled) setFilterLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [
        activeFocusCategoryKey,
        activeFocusOutcome,
        activeFocusSchemeSide,
        activeFocusSignature,
        message,
        t,
        taskId,
    ])

    const comparisonImages = useMemo(() => {
        if (!activeFocusOutcome) return images
        if (comparisonFilterResult?.signature !== activeFocusSignature) {
            return []
        }
        return images.filter((image) =>
            comparisonFilterResult.imageIds.has(image.id),
        )
    }, [
        activeFocusOutcome,
        activeFocusSignature,
        comparisonFilterResult,
        images,
    ])

    useEffect(() => {
        setComparisonImageId((currentImageId) =>
            comparisonImages.some((image) => image.id === currentImageId)
                ? currentImageId
                : comparisonImages[0]?.id,
        )
    }, [comparisonImages])

    useEffect(() => {
        if (!comparisonImageId) {
            setComparisonMatches([])
            return undefined
        }
        let cancelled = false
        setComparisonMatches([])
        void loadAllEvaluationMatches(taskId, {
            imageResultId: comparisonImageId,
        })
            .then((matches) => {
                if (!cancelled) setComparisonMatches(matches)
            })
            .catch((error) => {
                if (!cancelled) {
                    setComparisonMatches([])
                    message.error(
                        getModelManagementErrorDetail(
                            error,
                            t(
                                'admin:modelValidation.messages.loadComparisonFailed',
                            ),
                            t,
                        ),
                    )
                }
            })
        return () => {
            cancelled = true
        }
    }, [comparisonImageId, message, t, taskId])

    const selectedImage =
        comparisonImages.find((image) => image.id === comparisonImageId) || null
    const imagePath = getImageObjectKey(selectedImage)
    const normalizedKey = normalizeManagedObjectKey(imagePath)
    const selectedImageUrl =
        (normalizedKey ? imageUrlMap.get(normalizedKey) : null) ||
        getImageUrl(imagePath)
    const selectedFocusMatches =
        activeFocusOutcome &&
        comparisonFilterResult?.signature === activeFocusSignature
            ? comparisonFilterResult.matches.filter(
                  (match) => match.image_result_id === comparisonImageId,
              )
            : []
    const comparisonDisplayMatches = [
        ...new Map(
            [...comparisonMatches, ...selectedFocusMatches].map((match) => [
                match.id,
                match,
            ]),
        ).values(),
    ]
    const comparisonCategories = [
        ...new Set(
            comparisonDisplayMatches.flatMap(
                (match) =>
                    [
                        match.truth_category_key,
                        match.prediction_category_key,
                    ].filter(Boolean) as string[],
            ),
        ),
    ]
    const configuredLabelOptions = buildValidationLabelOptions(
        displayCategories,
        i18n.resolvedLanguage || i18n.language,
    )
    const comparisonLabelOptions: HistoryLabelOption[] =
        comparisonCategories.map((category, index) => {
            const configuredOption = configuredLabelOptions.find(
                (option) => option.display_category_key === category,
            )
            return configuredOption
                ? { ...configuredOption, cls_id: index }
                : {
                      cls_id: index,
                      class: category,
                      display_name: category,
                      eng_class: category,
                      display_category_key: category,
                  }
        })
    const describeMatch = (match: ModelEvaluationMatch): string => {
        if (match.outcome === 'misclassified') {
            return t('admin:modelValidation.report.misclassifiedRelation', {
                prediction: match.prediction_category_key || '-',
                truth: match.truth_category_key || '-',
            })
        }
        if (match.outcome === 'false_negative') {
            return t('admin:modelValidation.report.falseNegativeRelation', {
                truth: match.truth_category_key || '-',
            })
        }
        if (match.outcome === 'false_positive') {
            return t('admin:modelValidation.report.falsePositiveRelation', {
                prediction: match.prediction_category_key || '-',
            })
        }
        return t('admin:modelValidation.report.truePositiveRelation', {
            truth: match.truth_category_key || '-',
        })
    }
    const comparisonSchemeSides: ValidationSchemeSide[] = activeFocusSchemeSide
        ? [activeFocusSchemeSide]
        : ['baseline', 'candidate']
    const decoratePanel = (panel: {
        details: string[]
        emptyText?: string
        key: string
        label: string
        predictions: HistoryPrediction[]
    }) => {
        const panelCategories = new Set(
            panel.predictions.map(getPredictionCategory),
        )
        return {
            ...panel,
            labelOptions: comparisonLabelOptions.filter((option) =>
                panelCategories.has(
                    option.display_category_key ||
                        option.eng_class ||
                        option.class,
                ),
            ),
        }
    }
    const buildTruthPanel = (
        matches: ModelEvaluationMatch[],
        label = t('admin:modelValidation.scheme.truth'),
    ) =>
        decoratePanel({
            details: [],
            key: 'truth',
            label,
            predictions: deduplicatePredictions(
                matchesToPredictions(matches, true),
            ),
        })
    const buildSchemePanel = ({
        details,
        keySuffix,
        label,
        matches,
        side,
        showMissedTruth,
    }: {
        details: boolean
        keySuffix: string
        label: string
        matches: ModelEvaluationMatch[]
        side: ValidationSchemeSide
        showMissedTruth: boolean
    }) => {
        const sideMatches = matches.filter(
            (match) => match.scheme_side === side,
        )
        return decoratePanel({
            details: details
                ? [...new Set(sideMatches.map(describeMatch))]
                : [],
            key: `${side}-${keySuffix}`,
            label,
            predictions: deduplicatePredictions([
                ...matchesToPredictions(
                    sideMatches.filter(
                        (match) => match.outcome !== 'false_negative',
                    ),
                    false,
                ),
                ...(showMissedTruth
                    ? matchesToPredictions(
                          sideMatches.filter(
                              (match) => match.outcome === 'false_negative',
                          ),
                          true,
                      )
                    : []),
            ]),
        })
    }
    const schemeLabel = (side: ValidationSchemeSide) =>
        t(`admin:modelValidation.scheme.${side}`)
    const completeSchemePanels = comparisonSchemeSides.map((side) =>
        buildSchemePanel({
            details: false,
            keySuffix: 'complete',
            label: isPairwiseFocus
                ? t('admin:modelValidation.report.schemeComplete', {
                      scheme: schemeLabel(side),
                  })
                : schemeLabel(side),
            matches: comparisonMatches,
            side,
            showMissedTruth: false,
        }),
    )
    const problemSchemePanels = comparisonSchemeSides.map((side) =>
        buildSchemePanel({
            details: true,
            keySuffix: 'problem',
            label: t('admin:modelValidation.report.schemeProblem', {
                scheme: schemeLabel(side),
            }),
            matches: selectedFocusMatches,
            side,
            showMissedTruth: true,
        }),
    )
    const outcomeLabel = activeFocusOutcome
        ? t(`admin:modelValidation.outcome.${activeFocusOutcome}`)
        : ''
    const filteredComparisonColumns =
        activeFocusOutcome && !isPairwiseFocus
            ? [
                  {
                      key: 'truth',
                      label: t('admin:modelValidation.scheme.truth'),
                      panels: [
                          buildTruthPanel(
                              comparisonMatches,
                              t(
                                  'admin:modelValidation.report.completeRecognitionResults',
                              ),
                          ),
                      ],
                  },
                  ...(['baseline', 'candidate'] as ValidationSchemeSide[]).map(
                      (side) => {
                          const hasProblem = selectedFocusMatches.some(
                              (match) => match.scheme_side === side,
                          )
                          const problemLabel = t(
                              'admin:modelValidation.report.truthComparisonResult',
                              { outcome: outcomeLabel },
                          )
                          const problemPanel = hasProblem
                              ? buildSchemePanel({
                                    details: true,
                                    keySuffix: 'problem',
                                    label: problemLabel,
                                    matches: selectedFocusMatches,
                                    side,
                                    showMissedTruth: true,
                                })
                              : decoratePanel({
                                    details: [],
                                    emptyText: t(
                                        'admin:modelValidation.report.noSchemeProblem',
                                        { outcome: outcomeLabel },
                                    ),
                                    key: `${side}-problem-empty`,
                                    label: problemLabel,
                                    predictions: [],
                                })
                          return {
                              key: side,
                              label: schemeLabel(side),
                              panels: [
                                  buildSchemePanel({
                                      details: false,
                                      keySuffix: 'complete',
                                      label: t(
                                          'admin:modelValidation.report.completeRecognitionResults',
                                      ),
                                      matches: comparisonMatches,
                                      side,
                                      showMissedTruth: false,
                                  }),
                                  problemPanel,
                              ],
                          }
                      },
                  ),
              ]
            : []
    const comparisonGroups = !activeFocusOutcome
        ? [
              {
                  key: 'all',
                  title: undefined,
                  panels: [
                      buildTruthPanel(comparisonMatches),
                      ...completeSchemePanels,
                  ],
              },
          ]
        : isPairwiseFocus
          ? [
                {
                    key: 'metric-detail',
                    title: undefined,
                    panels: [
                        ...completeSchemePanels,
                        buildTruthPanel(comparisonMatches),
                        ...problemSchemePanels,
                    ],
                },
            ]
          : []
    const comparisonMatchSeed = comparisonDisplayMatches
        .map((match) => match.id)
        .join(',')
    const focusExplanation = activeFocusOutcome
        ? t(
              `admin:modelValidation.report.focusExplanation.${activeFocusOutcome}`,
          )
        : null
    const renderPanel = (panel: ReturnType<typeof decoratePanel>) => (
        <section className="model-evaluation-comparison__panel" key={panel.key}>
            <Typography.Title level={5}>{panel.label}</Typography.Title>
            {panel.details.length > 0 ? (
                <div className="model-evaluation-comparison__details">
                    {panel.details.map((detail) => (
                        <Typography.Text key={detail}>{detail}</Typography.Text>
                    ))}
                </div>
            ) : null}
            {panel.emptyText ? (
                <Empty
                    description={panel.emptyText}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : (
                <DetectionAnnotationEditor
                    containReadonlyPreview
                    dirty={false}
                    editable={false}
                    filename={selectedImage?.sample?.filename || '-'}
                    imageSrc={selectedImageUrl}
                    labelOptions={panel.labelOptions}
                    onChange={() => undefined}
                    onReset={() => undefined}
                    readonlyAnnotationStyle="model"
                    readonlyPreviewOnImageClick
                    previewMaskClosable={false}
                    showAnnotationLabels={false}
                    seedKey={`${comparisonImageId}:${panel.key}:${activeFocusSignature}:${comparisonMatchSeed}`}
                    seedPredictions={panel.predictions}
                />
            )}
        </section>
    )

    return (
        <div className="model-evaluation-comparison">
            <div
                className={`model-evaluation-comparison__toolbar${focus ? ' model-evaluation-comparison__toolbar--focused' : ''}`}
            >
                {!focus ? (
                    <div className="model-evaluation-comparison__field">
                        <Typography.Text type="secondary">
                            {t('admin:modelValidation.report.problemFilter')}
                        </Typography.Text>
                        <Select
                            aria-label={t(
                                'admin:modelValidation.report.problemFilter',
                            )}
                            onChange={setComparisonFilter}
                            options={[
                                {
                                    value: 'all',
                                    label: t(
                                        'admin:modelValidation.report.allImages',
                                    ),
                                },
                                ...problemOutcomes.map((value) => ({
                                    value,
                                    label: t(
                                        `admin:modelValidation.outcome.${value}`,
                                    ),
                                })),
                            ]}
                            value={comparisonFilter}
                        />
                    </div>
                ) : null}
                <div className="model-evaluation-comparison__field">
                    <Typography.Text type="secondary">
                        {t('admin:modelValidation.columns.image')}
                    </Typography.Text>
                    <Select
                        aria-label={t('admin:modelValidation.columns.image')}
                        disabled={
                            filterLoading || comparisonImages.length === 0
                        }
                        onChange={setComparisonImageId}
                        options={comparisonImages.map((image) => ({
                            value: image.id,
                            label:
                                image.sample?.filename ||
                                String(image.sample_id),
                        }))}
                        showSearch
                        value={comparisonImageId}
                    />
                </div>
            </div>
            {focusExplanation ? (
                <Alert
                    description={focusExplanation}
                    message={t(
                        `admin:modelValidation.outcome.${activeFocusOutcome}`,
                    )}
                    showIcon
                    type="info"
                />
            ) : null}
            <Spin spinning={loading || filterLoading}>
                {selectedImage ? (
                    filteredComparisonColumns.length > 0 ? (
                        <div className="model-evaluation-comparison__columns">
                            {filteredComparisonColumns.map((column) => (
                                <section
                                    aria-label={t(
                                        'admin:modelValidation.report.comparisonColumn',
                                        { scheme: column.label },
                                    )}
                                    className="model-evaluation-comparison__column"
                                    key={column.key}
                                >
                                    <Typography.Title
                                        className="model-evaluation-comparison__column-title"
                                        level={5}
                                    >
                                        {column.label}
                                    </Typography.Title>
                                    <div className="model-evaluation-comparison__panels">
                                        {column.panels.map(renderPanel)}
                                    </div>
                                </section>
                            ))}
                        </div>
                    ) : (
                        <div className="model-evaluation-comparison__groups">
                            {comparisonGroups.map((group) => (
                                <section
                                    className="model-evaluation-comparison__group"
                                    key={group.key}
                                >
                                    {group.title ? (
                                        <Typography.Text
                                            className="model-evaluation-comparison__group-title"
                                            strong
                                        >
                                            {group.title}
                                        </Typography.Text>
                                    ) : null}
                                    <div
                                        className={`model-evaluation-comparison__panels${group.panels.length === 2 ? ' model-evaluation-comparison__panels--two' : ''}`}
                                    >
                                        {group.panels.map(renderPanel)}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )
                ) : loading || filterLoading ? null : (
                    <Empty
                        description={t(
                            'admin:modelValidation.report.noMatchingImages',
                        )}
                    />
                )}
            </Spin>
        </div>
    )
}

export default ModelEvaluationComparison
